/** THE ONE REFUSAL NOTICE (Tom, 2026-10-10: a refused landing fails whole,
 *  and one plain-language notice says what failed and why). Commit, accept,
 *  merge, revert and a refused chat turn all show their refusal here, read
 *  into one shape by `toRefusal` / `refusalFromStep`.
 *
 *  It says what happened, lists what failed (a few by name, the rest
 *  counted; records the reader may not see are counted, never named), and
 *  offers the next steps the refusal names that this page can do. */

import React from "react";
import type { Refusal, RefusalIsland } from "@sourcepilot/ui/framework/data/refusal";
import { switchOrganisation } from "@sourcepilot/ui/framework/data/switch-organisation";
import { buildUrl } from "@sourcepilot/ui/framework/data/route-resolver";
import { navigateTo } from "@sourcepilot/ui/framework/data/navigate";
import { NotedConflictList, type NotedAlign } from "./NotedConflicts";
import { NOTED_WIRE } from "@sourcepilot/ui/framework/data/pins/noted-conflicts";

export type RefusalAction = { label: string; run: () => void; title?: string; disabled?: boolean };

/** "kg (Unit)", "Mass (Numeric attribute)"; an island the server sent no
 *  name for is not listed (it is counted in `others` by the server) */
const islandWords = (i: RefusalIsland) => {
  const spaced = i.type?.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  const type = spaced ? spaced.charAt(0) + spaced.slice(1).toLowerCase() : null;
  return type ? `${i.name} (${type})` : (i.name as string);
};

function IslandList({ label, islands, others = 0, testId }: { label: string; islands: RefusalIsland[]; others?: number; testId: string }) {
  const named = islands.filter((i) => !!i.name);
  if (!named.length && !others) return null;
  return (
    <div className="text-xs text-red-800" data-testid={testId}>
      <p>{label}</p>
      <ul className="list-disc space-y-0.5 pl-5">
        {named.map((i) => <li key={i.id} data-testid={`${testId}-item`}>{islandWords(i)}</li>)}
        {others > 0 && <li data-testid={`${testId}-others`}>and {others === 1 ? "1 other" : `${others} others`}</li>}
      </ul>
    </div>
  );
}

/** VC-D's pin_ambiguous refusal (a direct read of an island this page's
 *  references reach at different versions): the pin is made from the
 *  page's version notice, which knows the versions to choose from — this
 *  step points there and does nothing itself. */
export const PIN_IT_HERE_FROM_NOTICE: RefusalAction = {
  label: "Pin it here",
  run: () => {},
  title: "Choose the version in this page's notice about different versions",
  disabled: true,
};

/** what the notice can do itself, without its host */
const BUILT_IN: Record<string, (r: Refusal) => RefusalAction | undefined> = {
  reload: () => ({ label: "Reload", run: () => window.location.reload() }),
  // V26: the change belongs to another project (a chat edits only its own,
  // on main, a draft or a branch):
  // open that project to make it there (offered only when the reader may
  // read it; never without its page)
  open_island: (r) => (r.open?.type
    ? { label: r.open.name ? `Open ${r.open.name}` : "Open it", run: () => navigateTo(buildUrl(r.open!.type!, r.open!.id)) }
    : undefined),
  pin_it_yourself: () => PIN_IT_HERE_FROM_NOTICE,
  switch_organisation: (r) => (r.organisation?.id
    ? {
        // the session switches to where the record is, and the page reloads there
        label: r.organisation.name ? `Open in ${r.organisation.name}` : "Open in its organisation",
        run: () => { void switchOrganisation(r.organisation!.id); },
      }
    : { label: "Reload", run: () => window.location.reload() }),
};

const SHOWN = 5;

export function RefusalNotice({
  refusal,
  actions = {},
  onDismiss,
  children,
  testId = "refusal-notice",
  holderId,
  branch,
  alignNoted,
}: {
  refusal: Refusal | null | undefined;
  /** the host's handlers for `next` steps (`review_again`, …), by name */
  actions?: Record<string, RefusalAction | undefined>;
  onDismiss?: () => void;
  /** extra detail a host shows under the list (e.g. the values a re-base
   *  overrode) */
  children?: React.ReactNode;
  testId?: string;
  /** V33: the holder and lane a refusal's noted conflicts are settled in
   *  (each conflict's own, when the server names them, comes first) */
  holderId?: string;
  branch?: string;
  /** draws Align's preview for a noted conflict (organisms' alignNotedConflict) */
  alignNoted?: NotedAlign;
}) {
  if (!refusal) return null;
  const { failed, hidden = 0 } = refusal;
  const more = Math.max(0, failed.length - SHOWN) + (refusal.more ?? 0);
  const offered = refusal.next
    .map((n) => [n, actions[n] ?? BUILT_IN[n]?.(refusal)] as const)
    .filter((x): x is readonly [string, RefusalAction] => !!x[1]);
  return (
    <div
      className="space-y-1 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
      role="alert"
      data-testid={testId}
      data-code={refusal.code}
    >
      <p>{refusal.reason}</p>
      {refusal.code === "other_organisation" && refusal.organisation?.name && (
        <p className="text-xs">This is in {refusal.organisation.name}.</p>
      )}
      {(failed.length > 0 || hidden > 0) && (
        <ul className="list-disc space-y-0.5 pl-5 text-xs text-red-800" data-testid="refusal-failed">
          {failed.slice(0, SHOWN).map((f, i) => (
            <li key={i}>
              {f.what}
              {f.why && <span className="text-red-700/80">: {f.why}</span>}
            </li>
          ))}
          {more > 0 && <li>and {more} more</li>}
          {hidden > 0 && (
            <li>
              {hidden === 1 ? "1 record" : `${hidden} records`} you cannot see
            </li>
          )}
        </ul>
      )}
      {refusal.holders || (refusal.code === "island_in_use" && refusal.others)
        ? <IslandList label="Used by:" islands={refusal.holders ?? []} others={refusal.others} testId="refusal-holders" />
        : refusal.islands && <IslandList label="Concerns:" islands={refusal.islands} others={refusal.others} testId="refusal-islands" />}
      {/* pin_links_broken: the records the links would point into, missing
          at that version; one the reader may not see is counted */}
      {(refusal.records?.length || refusal.hiddenRecords) && (
        <div className="text-xs text-red-800" data-testid="refusal-records">
          <p>Links into records missing at that version:</p>
          <ul className="list-disc space-y-0.5 pl-5">
            {refusal.records?.map((r, i) => <li key={i} data-testid="refusal-records-item">{r}</li>)}
            {!!refusal.hiddenRecords && <li data-testid="refusal-records-hidden">{refusal.hiddenRecords === 1 ? "1 record you can't see" : `${refusal.hiddenRecords} records you can't see`}</li>}
          </ul>
        </div>
      )}
      {/* commit / accept / merge blocked by noted conflicts (VC-D: each names
          its lane, `branch`): each, to settle; conflicts named without a lane
          (an undo or pin refused where saving commits): what it would leave */}
      {refusal.noted && <NotedConflictList conflicts={refusal.noted} holderId={holderId} branch={branch} alignWith={alignNoted} testId="refusal-noted"
        {...(refusal.code === NOTED_WIRE.refusalCode && refusal.noted.every((c) => !!c.branch) ? {} : { wouldLeave: "It would leave" })} />}
      {children}
      {refusal.ref && (
        <p className="text-xs text-red-800/80" data-testid="refusal-ref">
          Reference: {refusal.ref}
        </p>
      )}
      {(offered.length > 0 || onDismiss) && (
        <div className="flex gap-2 pt-1">
          {offered.map(([name, a]) => (
            <button
              key={name}
              onClick={a.run}
              title={a.title}
              disabled={a.disabled}
              data-testid={`refusal-next-${name}`}
              className="rounded bg-red-700 px-2 py-1 text-xs text-white disabled:opacity-50"
            >
              {a.label}
            </button>
          ))}
          {onDismiss && (
            <button onClick={onDismiss} className="rounded px-2 py-1 text-xs text-red-800 underline" data-testid="refusal-dismiss">
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}
